/**
 * Community Audit Metrics Chart Component
 *
 * Displays a multi-line chart showing accuracy, precision, recall, and f1 score over time.
 * Handles data processing, SVG path generation, and loading states.
 */

import type { AuditReport } from '@internal-audit/domain';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { BaseItem, Shimmer, Text } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import { AUDIT_METRIC_COLORS } from '../shared/constants/audit-metric-colors';

import { ChartHeader } from './CommunityAuditAccuracyChart/ChartHeader';
import { MetricsChart } from './CommunityAuditAccuracyChart/MetricsChart';
import * as styles from './CommunityAuditAccuracyChart.css';
import * as shimmerStyles from './shared/shimmer.css';

interface MetricsDataPoint {
  date: string;
  accuracy: number; // 0-100
  precision: number; // 0-100
  recall: number; // 0-100
  f1Score: number; // 0-100
}

interface CommunityAuditAccuracyChartProps {
  auditSessions: FlexibleAuditSession[];
  auditReports?: AuditReport[]; // Optional audit reports for full metrics
  isLoading?: boolean;
}

export function CommunityAuditAccuracyChart({ auditSessions, auditReports = [], isLoading = false }: CommunityAuditAccuracyChartProps) {
  // Generate metrics data from audit reports (preferred) or fallback to audit sessions (accuracy only)
  const metricsData: MetricsDataPoint[] = useMemo(() => {
    if (isLoading) return [];

    // If we have audit reports with metrics, use those for full multi-line chart
    if (auditReports.length > 0) {
      const reportsWithMetrics = auditReports
        .filter(report => report.metrics?.overall) // Accept any report with metrics, regardless of status
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      if (reportsWithMetrics.length > 0) {
        return reportsWithMetrics
          .map(report => {
            const metrics = report.metrics?.overall;

            if (!metrics) return null;

            return {
              date: report.createdAt,
              accuracy: Math.round(metrics.accuracy),
              precision: Math.round(metrics.precision),
              recall: Math.round(metrics.recall),
              f1Score: Math.round(metrics.f1Score),
            };
          })
          .filter((item): item is MetricsDataPoint => item !== null);
      }
    }

    // Check for audit sessions with accuracy data
    const completedSessions = auditSessions
      .filter(
        (session): session is FlexibleAuditSession & { accuracy: number; completedAt: string } =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          typeof (session as any).accuracy === 'number' && typeof session.completedAt === 'string'
      )
      .sort((a, b) => new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime());

    if (completedSessions.length > 0) {
      return completedSessions.map(session => ({
        date: session.completedAt,
        accuracy: session.accuracy,
        precision: session.accuracy, // Fallback: use accuracy as approximation
        recall: session.accuracy, // Fallback: use accuracy as approximation
        f1Score: session.accuracy, // Fallback: use accuracy as approximation
      }));
    }

    // If no real data available, return empty array (no mock data)
    return [];
  }, [auditSessions, auditReports, isLoading]);

  // Generate SVG paths for multi-line chart
  const chartData = useMemo(() => {
    if (isLoading || metricsData.length < 1)
      return {
        accuracy: { pathData: '', points: [] },
        precision: { pathData: '', points: [] },
        recall: { pathData: '', points: [] },
        f1Score: { pathData: '', points: [] },
      };

    const width = 300; // SVG width
    const height = 200; // SVG height
    const padding = 16;

    // Use fixed 0-100 range for percentage metrics (values are already normalized to 0-100 by BFF)
    const minValue = 0;
    const range = 100;

    // Generate paths for each metric
    const metrics = ['accuracy', 'precision', 'recall', 'f1Score'] as const;
    const result = {} as Record<(typeof metrics)[number], { pathData: string; points: Array<{ x: number; y: number; value: number }> }>;

    metrics.forEach(metric => {
      const points = metricsData.map((point, index) => {
        // For single data point, position at right edge ("Now") since the X axis is time-based
        const x = metricsData.length === 1 ? width - padding : padding + (index / (metricsData.length - 1)) * (width - 2 * padding);
        const y = height - padding - ((point[metric] - minValue) / range) * (height - 2 * padding);

        return { x, y, value: point[metric] };
      });

      // For single data point, create a small circle instead of a line
      const pathData =
        metricsData.length === 1
          ? `M ${points[0].x - 2} ${points[0].y} A 2 2 0 1 1 ${points[0].x + 2} ${points[0].y} A 2 2 0 1 1 ${points[0].x - 2} ${points[0].y}`
          : points.reduce((path, point, index) => {
              if (index === 0) {
                return `M ${point.x} ${point.y}`;
              }

              return `${path} L ${point.x} ${point.y}`;
            }, '');

      result[metric] = { pathData, points };
    });

    return result;
  }, [metricsData, isLoading]);

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.graphPanel}>
        <Text className={styles.title} size="base" weight="medium">
          <Shimmer className={shimmerStyles.shimmerHeader} />
        </Text>
        <BaseItem as="button" background="visible" className={styles.sparklineContainer} type="button">
          <Shimmer className={shimmerStyles.shimmerChart} />
          <div className={styles.timeLabels}>
            <Shimmer className={shimmerStyles.shimmerTimeLabel} />
            <Shimmer className={shimmerStyles.shimmerTimeValue} />
          </div>
          <div className={styles.chartLegend}>
            <Shimmer className={shimmerStyles.shimmerLabel} />
            <Shimmer className={shimmerStyles.shimmerLabel} />
            <Shimmer className={shimmerStyles.shimmerLabel} />
            <Shimmer className={shimmerStyles.shimmerLabel} />
          </div>
        </BaseItem>
      </div>
    );
  }

  return (
    <div className={styles.graphPanel}>
      <ChartHeader />
      <BaseItem as="button" background="visible" className={styles.sparklineContainer} type="button">
        <MetricsChart chartData={chartData} isLoading={isLoading} />
        {/* Time labels inside the card so all panels have same structure: title + card */}
        <div className={styles.timeLabels}>
          <Text color="secondary" size="xs">
            1 Year Ago
          </Text>
          <Text color="secondary" size="xs">
            Now
          </Text>
        </div>
        {/* Legend inside the card using shared constants */}
        <div className={styles.chartLegend}>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.accuracy }} />
            <Text className={styles.legendLabel} color="secondary" size="xs">
              Accuracy
            </Text>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.precision }} />
            <Text className={styles.legendLabel} color="secondary" size="xs">
              Precision
            </Text>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.recall }} />
            <Text className={styles.legendLabel} color="secondary" size="xs">
              Recall
            </Text>
          </div>
          <div className={styles.legendItem}>
            <div className={styles.legendColor} style={{ backgroundColor: AUDIT_METRIC_COLORS.f1Score }} />
            <Text className={styles.legendLabel} color="secondary" size="xs">
              F1 Score
            </Text>
          </div>
        </div>
      </BaseItem>
    </div>
  );
}
